import React, { useState, useEffect } from 'react';
import { requestNotificationPermission, checkNotificationSupport } from './notificationService';

export default function AdminPanel() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [error, setError] = useState('');
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(true);

  const ADMIN_PASSWORD = 'Garud@2024'; // Change this!

  // Check if admin panel should be shown on load
  useEffect(() => {
    const shouldShowPanel = localStorage.getItem('showAdminPanelOnLoad');
    if (shouldShowPanel === 'true') {
      setShowLoginPrompt(true);
      localStorage.removeItem('showAdminPanelOnLoad');
    }
  }, []);

  // Load submissions from server
  useEffect(() => {
    if (isLoggedIn) {
      const fetchSubmissions = async () => {
        try {
          const response = await fetch('/api/submissions');
          if (response.ok) {
            const data = await response.json();
            setSubmissions(data.submissions);
          } else {
            // Fallback to localStorage if server fails
            const saved = localStorage.getItem('contactSubmissions');
            if (saved) {
              setSubmissions(JSON.parse(saved));
            }
          }
        } catch (error) {
          console.error('Error fetching submissions:', error);
          // Fallback to localStorage if server is unavailable
          const saved = localStorage.getItem('contactSubmissions');
          if (saved) {
            setSubmissions(JSON.parse(saved));
          }
        }
      };
      
      fetchSubmissions();
      
      // Check notification support and show prompt only if not already decided
      if (checkNotificationSupport()) {
        // Check if already enabled
        if (window.Notification && window.Notification.permission === 'granted') {
          setNotificationEnabled(true);
          // Don't show prompt if already granted
        } else if (window.Notification && window.Notification.permission === 'default') {
          // Only show prompt if permission hasn't been asked yet (default state)
          setShowNotificationPrompt(true);
        }
        // If permission === 'denied', don't show prompt (user already refused)
      }
    }
  }, [isLoggedIn]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsLoggedIn(true);
      setPassword('');
      setError('');
      setShowLoginPrompt(false);
    } else {
      setError('Incorrect Password!');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setPassword('');
    setShowNotificationPrompt(false);
  };

  const handleCloseLoginPrompt = () => {
    setShowLoginPrompt(false);
    setPassword('');
    setError('');
  };

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    setNotificationEnabled(granted);
    setShowNotificationPrompt(false);
    
    if (granted) {
      // Send a test notification
      new Notification('Notifications Enabled! ðŸŽ‰', {
        icon: '/img/favicon.ico',
        body: 'You will now receive notifications for new form submissions.',
      });
    }
  };

  const handleSkipNotifications = () => {
    setShowNotificationPrompt(false);
  };

  const deleteSubmission = async (index) => {
    const submissionId = submissions[index].id;
    
    try {
      const response = await fetch(`/api/submissions/${submissionId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        // Update local state
        const updated = submissions.filter((_, i) => i !== index);
        setSubmissions(updated);
        // Also update localStorage
        localStorage.setItem('contactSubmissions', JSON.stringify(updated));
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      // Fallback: delete from localStorage only
      const updated = submissions.filter((_, i) => i !== index);
      setSubmissions(updated);
      localStorage.setItem('contactSubmissions', JSON.stringify(updated));
    }
  };

  const clearAll = async () => {
    if (window.confirm('Clear all submissions?')) {
      try {
        const response = await fetch('/api/submissions', {
          method: 'DELETE'
        });
        
        if (response.ok) {
          setSubmissions([]);
          localStorage.removeItem('contactSubmissions');
        }
      } catch (error) {
        console.error('Error clearing submissions:', error);
        // Fallback: clear localStorage only
        setSubmissions([]);
        localStorage.removeItem('contactSubmissions');
      }
    }
  };

  if (!isLoggedIn && !showLoginPrompt) {
    return null;
  }

  if (!isLoggedIn && showLoginPrompt) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-sm border border-gray-200">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-gray-900 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-xl font-extrabold text-gray-900">Admin Access</h3>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">Enter Restricted Area</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter Password"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all text-center tracking-widest font-medium outline-none text-gray-900"
                autoFocus
              />
              {error && <p className="text-red-500 text-xs font-bold text-center mt-2 animate-pulse">{error}</p>}
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-gray-900 to-black text-amber-500 hover:text-amber-400 text-sm font-bold uppercase tracking-widest rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              Login to Dashboard
            </button>
          </form>
          
          <button
            onClick={handleCloseLoginPrompt}
            className="w-full mt-3 py-2 text-gray-500 hover:text-gray-700 text-xs font-medium uppercase tracking-wider"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-sm border border-gray-200">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-gray-900 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-xl font-extrabold text-gray-900">Admin Access</h3>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">Enter Restricted Area</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter Password"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all text-center tracking-widest font-medium outline-none text-gray-900"
              />
              {error && <p className="text-red-500 text-xs font-bold text-center mt-2 animate-pulse">{error}</p>}
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-gray-900 to-black text-amber-500 hover:text-amber-400 text-sm font-bold uppercase tracking-widest rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              Login to Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-gray-100 flex flex-col md:flex-row">
      {/* Sidebar */}
      <div className="w-full md:w-64 bg-gray-900 text-white flex flex-col shadow-2xl">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between md:justify-center">
          <span className="font-bold tracking-widest uppercase text-sm">Admin Panel</span>
          <button onClick={handleLogout} className="md:hidden text-gray-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-grow p-4 space-y-2 overflow-y-auto">
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-500/10 text-amber-500 rounded-xl font-bold text-sm border border-amber-500/20">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Dashboard
          </div>
        </div>

        <div className="p-4 border-t border-gray-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-3 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Log Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow flex flex-col h-full overflow-hidden">
        <header className="bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <h2 className="text-xl font-extrabold text-gray-900">Welcome back, Admin</h2>
          <div className="text-sm text-gray-600">
            <span className="font-bold text-amber-600">{submissions.length}</span> Submissions
          </div>
        </header>

        <div className="flex-grow overflow-y-auto p-6 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <span className="text-gray-500 text-xs font-bold uppercase tracking-wider">Total Submissions</span>
              <span className="text-3xl font-extrabold text-gray-900 block mt-2">{submissions.length}</span>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <span className="text-gray-500 text-xs font-bold uppercase tracking-wider">From Contact Page</span>
              <span className="text-3xl font-extrabold text-blue-600 block mt-2">
                {submissions.filter(s => s.source === 'contact_page').length}
              </span>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <span className="text-gray-500 text-xs font-bold uppercase tracking-wider">From Homepage</span>
              <span className="text-3xl font-extrabold text-green-600 block mt-2">
                {submissions.filter(s => s.source === 'home_page').length}
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-extrabold text-gray-900">Form Submissions</h3>
              {submissions.length > 0 && (
                <button
                  onClick={clearAll}
                  className="text-xs font-bold text-red-600 hover:text-red-700 uppercase tracking-widest"
                >
                  Clear All
                </button>
              )}
            </div>

            {submissions.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500 text-sm">No submissions yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="p-4 font-bold border-b border-gray-100">Name</th>
                      <th className="p-4 font-bold border-b border-gray-100">Email / Phone</th>
                      <th className="p-4 font-bold border-b border-gray-100">Message</th>
                      <th className="p-4 font-bold border-b border-gray-100">Date</th>
                      <th className="p-4 font-bold border-b border-gray-100">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((sub, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="p-4 font-bold text-gray-900">{sub.firstName} {sub.lastName}</td>
                        <td className="p-4">
                          <div className="text-sm">{sub.email}</div>
                          {sub.phone && <div className="text-xs text-gray-500">{sub.phone}</div>}
                        </td>
                        <td className="p-4">
                          <div className="text-xs text-gray-600 line-clamp-2">{sub.message}</div>
                          {sub.service && <div className="text-xs text-amber-600 mt-1">Service: {sub.service}</div>}
                        </td>
                        <td className="p-4 text-xs text-gray-500">
                          {new Date(sub.submissionDate).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => deleteSubmission(idx)}
                            className="text-red-600 hover:text-red-700 text-xs font-bold"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification Permission Modal */}
      {showNotificationPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-sm border border-gray-200 animate-in">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Enable Notifications</h3>
              <p className="text-gray-600 text-sm mb-6">
                Get instant notifications when users submit the contact form. Stay updated in real-time!
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-xs text-amber-900 font-medium">
                  âœ“ Get notified for new form submissions<br/>
                  âœ“ See user details in notification<br/>
                  âœ“ Never miss an opportunity
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSkipNotifications}
                  className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-900 text-sm font-bold uppercase tracking-wider rounded-lg transition-colors"
                >
                  Skip for Now
                </button>
                <button
                  onClick={handleEnableNotifications}
                  className="flex-1 py-2.5 px-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-sm font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg hover:shadow-amber-500/50"
                >
                  Enable Notifications
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
